import { useTranslations } from '@vocab/react';
import {
  Inline,
  OverflowMenu,
  MenuItem,
  Box,
  ButtonIcon,
  IconDelete,
  IconEdit,
  Link,
} from 'braid-design-system';

import { BASE_URL } from 'src/constants/urls';
import type { UserDetails } from 'src/graphql/generated';
import { useAccountContext } from 'src/hooks/use-account-context';
import { useUserActions } from 'src/hooks/use-user-actions';
import { useConfig } from 'src/modules/ConfigProvider';
import {
  trackClickDeleteUser,
  trackClickReSendRegistrationLink,
  trackClickSendPasswordResetLink,
} from 'src/utils/trackingUserActions';

import translations from '../.vocab';

import type { UserRowProps } from './UserItem';

export const UserActions = ({ user, removeUser }: UserRowProps) => {
  const { t } = useTranslations(translations);
  const { urlResolver } = useConfig();
  const { data } = useAccountContext();
  const { handleSendPasswordResetEmail, handleResendActivationEmail } =
    useUserActions();

  const currentUser = data?.headerFooterContext?.user;

  const resendActivationEmail = () => {
    trackClickReSendRegistrationLink(user.id);
    handleResendActivationEmail(user as UserDetails);
  };

  const sendPasswordResetEmail = () => {
    trackClickSendPasswordResetLink(user.id);
    handleSendPasswordResetEmail(user as UserDetails);
  };

  return (
    <Box
      display="flex"
      justifyContent={{ mobile: 'spaceBetween', tablet: 'flexEnd' }}
      alignItems="center"
    >
      <Inline space="medium">
        {currentUser?.id !== user.id && (
          <ButtonIcon
            onClick={() => {
              trackClickDeleteUser(user.id);
              removeUser(user);
            }}
            icon={<IconDelete />}
            label={t('Remove user')}
            id="remove-user"
          />
        )}
        <Link href={urlResolver({ path: `/${BASE_URL}/edit/${user.id}` })}>
          <ButtonIcon
            onClick={() => {}}
            icon={<IconEdit />}
            label={t('Edit user')}
            id="edit-user"
          />
        </Link>
      </Inline>
      <Box paddingLeft="large">
        <OverflowMenu label="Actions">
          <MenuItem
            onClick={() =>
              user.status === 'Pending'
                ? resendActivationEmail()
                : sendPasswordResetEmail()
            }
          >
            {user.status === 'Pending'
              ? t('Resend activation')
              : t('Reset password')}
          </MenuItem>
        </OverflowMenu>
      </Box>
    </Box>
  );
};
